import { gql } from '@apollo/client'

const INBOX_FIELDS = gql`
  fragment InboxNotificationFields on InboxNotification {
    id
    categoryKey
    severity
    subject {
      type
      code
    }
    title
    body
    occurredAt
    readAt
    archivedAt
  }
`

const DELIVERY_FIELDS = gql`
  fragment NotificationDeliveryFields on NotificationDelivery {
    id
    notificationId
    categoryKey
    channel
    status
    attemptNo
    destinationMasked
    errorCode
    sentAt
    completedAt
  }
`

export const NOTIFICATION_CAPABILITIES_QUERY = gql`
  query NotificationCapabilities {
    notificationCapabilities {
      adminRead
      adminManage
      providerRead
      providerManage
      globalScope
    }
  }
`

export const NOTIFICATION_INBOX_QUERY = gql`
  ${INBOX_FIELDS}
  query NotificationInbox(
    $pageSize: Int
    $pageToken: String
    $unreadOnly: Boolean
    $categoryKey: String
  ) {
    notificationInbox(
      pageSize: $pageSize
      pageToken: $pageToken
      unreadOnly: $unreadOnly
      categoryKey: $categoryKey
    ) {
      notifications {
        ...InboxNotificationFields
      }
      nextPageToken
    }
  }
`

export const NOTIFICATION_UNREAD_COUNT_QUERY = gql`
  query NotificationUnreadCount {
    notificationUnreadCount
  }
`

export const NOTIFICATION_DELIVERY_HISTORY_QUERY = gql`
  ${DELIVERY_FIELDS}
  query NotificationDeliveryHistory(
    $pageSize: Int
    $pageToken: String
    $categoryKey: String
    $channel: NotificationChannel
    $status: NotificationDeliveryStatus
    $from: String
    $to: String
  ) {
    notificationDeliveryHistory(
      pageSize: $pageSize
      pageToken: $pageToken
      categoryKey: $categoryKey
      channel: $channel
      status: $status
      from: $from
      to: $to
    ) {
      deliveries {
        ...NotificationDeliveryFields
      }
      nextPageToken
    }
  }
`

export const NOTIFICATION_PREFERENCES_QUERY = gql`
  query NotificationPreferences($pageSize: Int, $pageToken: String) {
    notificationPreferences(pageSize: $pageSize, pageToken: $pageToken) {
      preferences {
        categoryKey
        channel
        enabled
        userMutable
      }
      page {
        nextPageToken
      }
    }
  }
`

// IAM-owned channel consent is intentionally separate from notification-service
// category preferences. The latter controls delivery rules; this controls the
// viewer's identity/profile-level consent for each delivery pipe.
export const IAM_NOTIFICATION_CHANNELS_QUERY = gql`
  query MyNotificationChannels {
    myNotificationChannels {
      inAppEnabled
      emailEnabled
      smsEnabled
    }
  }
`

export const UPDATE_IAM_NOTIFICATION_CHANNELS_MUTATION = gql`
  mutation UpdateMyNotificationChannels($input: NotificationChannelsInput!) {
    updateMyNotificationChannels(input: $input) {
      inAppEnabled
      emailEnabled
      smsEnabled
    }
  }
`

export const NOTIFICATION_ADMIN_CATEGORIES_QUERY = gql`
  query NotificationAdminCategories($pageSize: Int, $pageToken: String) {
    notificationAdminCategories(pageSize: $pageSize, pageToken: $pageToken) {
      categories {
        key
        domain
        defaultSeverity
        defaultChannels
        userMutable
        retainBody
        subjectKind
        variableContract
        displayName
      }
      page {
        nextPageToken
      }
    }
  }
`

export const NOTIFICATION_ADMIN_RULES_QUERY = gql`
  query NotificationAdminRules(
    $pageSize: Int
    $pageToken: String
    $eventType: String
    $categoryKey: String
    $active: Boolean
  ) {
    notificationAdminRules(
      pageSize: $pageSize
      pageToken: $pageToken
      eventType: $eventType
      categoryKey: $categoryKey
      active: $active
    ) {
      rules {
        id
        eventType
        schemaVersion
        categoryKey
        active
        description
        conditionExpression
        subjectExpression
        variableExpressions
        groupKeyExpression
        subscriptionLookups {
          entityExpression
          includeParentScopes
        }
      }
      page {
        nextPageToken
      }
    }
  }
`

export const NOTIFICATION_CHANNEL_HEALTH_QUERY = gql`
  query NotificationChannelHealth(
    $scope: NotificationEntityInput
    $pageSize: Int
    $pageToken: String
  ) {
    notificationChannelHealth(
      scope: $scope
      pageSize: $pageSize
      pageToken: $pageToken
    ) {
      health {
        channel
        providerKey
        providerConfigId
        scope {
          type
          code
        }
        status
        creditRemaining
        successRate1h
        successRate24h
        pendingCount
        failedLastHour
        dlqDepth
        circuit
        lastSuccessAt
        lastVerifiedAt
        lastErrorCode
      }
      page {
        nextPageToken
      }
    }
  }
`

export const NOTIFICATION_ADMIN_SUBSCRIPTIONS_QUERY = gql`
  query NotificationAdminSubscriptions(
    $entity: NotificationEntityInput
    $categoryKey: String
    $pageSize: Int
    $pageToken: String
  ) {
    notificationAdminSubscriptions(
      entity: $entity
      categoryKey: $categoryKey
      pageSize: $pageSize
      pageToken: $pageToken
    ) {
      subscriptions {
        id
        entity {
          type
          code
        }
        categoryKey
        channel
        active
        bindings {
          id
          active
          kind
          value
        }
      }
      page {
        nextPageToken
      }
    }
  }
`

export const NOTIFICATION_PROVIDER_CONFIGS_QUERY = gql`
  query NotificationProviderConfigs(
    $scope: NotificationEntityInput
    $channel: NotificationChannel
    $pageSize: Int
    $pageToken: String
  ) {
    notificationProviderConfigs(
      scope: $scope
      channel: $channel
      pageSize: $pageSize
      pageToken: $pageToken
    ) {
      configs {
        id
        channel
        providerKey
        scope {
          type
          code
        }
        settings
        credentialFingerprint
        active
        lastVerifiedAt
      }
      page {
        nextPageToken
      }
    }
  }
`

export const NOTIFICATION_TEMPLATES_QUERY = gql`
  query NotificationTemplates(
    $categoryKey: String
    $channel: NotificationChannel
    $locale: String
    $pageSize: Int
    $pageToken: String
  ) {
    notificationTemplates(
      categoryKey: $categoryKey
      channel: $channel
      locale: $locale
      pageSize: $pageSize
      pageToken: $pageToken
    ) {
      templates {
        id
        categoryKey
        channel
        locale
        version
        subject
        body
        variables
        active
      }
      page {
        nextPageToken
      }
    }
  }
`

export const NOTIFICATION_ADMIN_DELIVERIES_QUERY = gql`
  ${DELIVERY_FIELDS}
  query NotificationAdminDeliveries(
    $notificationId: ID
    $recipientRef: String
    $entity: NotificationEntityInput
    $categoryKey: String
    $channel: NotificationChannel
    $providerConfigId: ID
    $status: NotificationDeliveryStatus
    $from: String
    $to: String
    $pageSize: Int
    $pageToken: String
  ) {
    notificationAdminDeliveries(
      notificationId: $notificationId
      recipientRef: $recipientRef
      entity: $entity
      categoryKey: $categoryKey
      channel: $channel
      providerConfigId: $providerConfigId
      status: $status
      from: $from
      to: $to
      pageSize: $pageSize
      pageToken: $pageToken
    ) {
      deliveries {
        ...NotificationDeliveryFields
      }
      page {
        nextPageToken
      }
    }
  }
`

export const NOTIFICATION_PREFERENCE_CHANGES_QUERY = gql`
  query NotificationPreferenceChanges(
    $userId: ID!
    $pageSize: Int
    $pageToken: String
  ) {
    notificationPreferenceChanges(
      userId: $userId
      pageSize: $pageSize
      pageToken: $pageToken
    ) {
      changes {
        id
        userId
        categoryKey
        channel
        oldEnabled
        newEnabled
        changedBy
        changedAt
      }
      page {
        nextPageToken
      }
    }
  }
`

export const NOTIFICATION_RECEIVED_SUBSCRIPTION = gql`
  subscription NotificationReceived {
    notificationReceived {
      notificationId
    }
  }
`

export const MARK_NOTIFICATIONS_READ_MUTATION = gql`
  ${INBOX_FIELDS}
  mutation MarkNotificationsRead($ids: [ID!]!) {
    markNotificationsRead(ids: $ids) {
      ...InboxNotificationFields
    }
  }
`
export const MARK_ALL_NOTIFICATIONS_READ_MUTATION = gql`
  mutation MarkAllNotificationsRead {
    markAllNotificationsRead
  }
`
export const ARCHIVE_NOTIFICATIONS_MUTATION = gql`
  mutation ArchiveNotifications($ids: [ID!]!) {
    archiveNotifications(ids: $ids)
  }
`
export const UPDATE_NOTIFICATION_PREFERENCES_MUTATION = gql`
  mutation UpdateNotificationPreferences(
    $preferences: [NotificationPreferenceInput!]!
    $idempotencyKey: String!
  ) {
    updateNotificationPreferences(
      preferences: $preferences
      idempotencyKey: $idempotencyKey
    ) {
      categoryKey
      channel
      enabled
      userMutable
    }
  }
`
export const UPSERT_NOTIFICATION_CATEGORY_MUTATION = gql`
  mutation UpsertNotificationCategory(
    $category: NotificationCategoryInput!
    $idempotencyKey: String!
  ) {
    upsertNotificationCategory(
      category: $category
      idempotencyKey: $idempotencyKey
    ) {
      key
      domain
      defaultSeverity
      defaultChannels
      userMutable
      retainBody
      subjectKind
      variableContract
      displayName
    }
  }
`
export const UPSERT_NOTIFICATION_RULE_MUTATION = gql`
  mutation UpsertNotificationRule(
    $rule: NotificationEventRuleInput!
    $idempotencyKey: String!
  ) {
    upsertNotificationRule(rule: $rule, idempotencyKey: $idempotencyKey) {
      id
      eventType
      schemaVersion
      categoryKey
      active
      description
    }
  }
`
export const DELETE_NOTIFICATION_RULE_MUTATION = gql`
  mutation DeleteNotificationRule($id: ID!, $idempotencyKey: String!) {
    deleteNotificationRule(id: $id, idempotencyKey: $idempotencyKey)
  }
`
export const SIMULATE_NOTIFICATION_RULE_MUTATION = gql`
  mutation SimulateNotificationRule($id: ID!, $event: JSONObject!) {
    simulateNotificationRule(id: $id, event: $event) {
      matched
      subject {
        type
        code
      }
      recipients
      projectedPayload
      validationErrors
    }
  }
`
export const UPSERT_NOTIFICATION_SUBSCRIPTION_MUTATION = gql`
  mutation UpsertNotificationSubscription(
    $subscription: NotificationSubscriptionInput!
    $idempotencyKey: String!
  ) {
    upsertNotificationSubscription(
      subscription: $subscription
      idempotencyKey: $idempotencyKey
    ) {
      id
      entity {
        type
        code
      }
      categoryKey
      channel
      active
      bindings {
        id
        active
        kind
        value
      }
    }
  }
`
export const DELETE_NOTIFICATION_SUBSCRIPTION_MUTATION = gql`
  mutation DeleteNotificationSubscription($id: ID!, $idempotencyKey: String!) {
    deleteNotificationSubscription(id: $id, idempotencyKey: $idempotencyKey)
  }
`
export const UPSERT_NOTIFICATION_PROVIDER_CONFIG_MUTATION = gql`
  mutation UpsertNotificationProviderConfig(
    $config: NotificationProviderConfigInput!
    $credentials: JSONObject!
    $idempotencyKey: String!
  ) {
    upsertNotificationProviderConfig(
      config: $config
      credentials: $credentials
      idempotencyKey: $idempotencyKey
    ) {
      id
      channel
      providerKey
      scope {
        type
        code
      }
      settings
      credentialFingerprint
      active
      lastVerifiedAt
    }
  }
`
export const VERIFY_NOTIFICATION_PROVIDER_CONFIG_MUTATION = gql`
  mutation VerifyNotificationProviderConfig(
    $id: ID!
    $idempotencyKey: String!
  ) {
    verifyNotificationProviderConfig(id: $id, idempotencyKey: $idempotencyKey) {
      valid
      errorCode
      verifiedAt
    }
  }
`
export const SET_NOTIFICATION_PROVIDER_POLICY_MUTATION = gql`
  mutation SetNotificationProviderPolicy(
    $policy: NotificationProviderPolicyInput!
    $idempotencyKey: String!
  ) {
    setNotificationProviderPolicy(
      policy: $policy
      idempotencyKey: $idempotencyKey
    ) {
      entity {
        type
        code
      }
      channel
      mode
    }
  }
`
export const UPSERT_NOTIFICATION_TEMPLATE_MUTATION = gql`
  mutation UpsertNotificationTemplate(
    $template: NotificationTemplateInput!
    $idempotencyKey: String!
  ) {
    upsertNotificationTemplate(
      template: $template
      idempotencyKey: $idempotencyKey
    ) {
      id
      categoryKey
      channel
      locale
      version
      subject
      body
      variables
      active
    }
  }
`
export const PREVIEW_NOTIFICATION_TEMPLATE_MUTATION = gql`
  mutation PreviewNotificationTemplate($id: ID!, $variables: JSONObject!) {
    previewNotificationTemplate(id: $id, variables: $variables) {
      subject
      body
    }
  }
`
export const RETRY_NOTIFICATION_DELIVERY_MUTATION = gql`
  ${DELIVERY_FIELDS}
  mutation RetryNotificationDelivery($id: ID!, $idempotencyKey: String!) {
    retryNotificationDelivery(id: $id, idempotencyKey: $idempotencyKey) {
      ...NotificationDeliveryFields
    }
  }
`
